import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import ThinkingText from '../../views/common/ThinkingText';
import ChapterDetailTitle from './ChapterDetailTitle';
import ChapterList from './ChapterList';
import ControlPanel from './control/ControlPanel';
import LessonPlayer, { type LessonPlayerRef } from './LessonPlayer';
import { ChapterProvider, useChapterStore } from './provider/ChapterProvider';
import { LessonProvider, useLessonStore } from './provider/LessonProvider';
import { SentenceProvider } from './provider/SentenceProvider';
import SentenceLearning from './SentenceLearning';
import CountdownText from '../../views/common/CountdownText';
import ListeningHeader from '../ListeningHeader';

/**
 * ChapterSection: 챕터 콘텐츠 영역
 * - SentenceProvider로 현재 문장 제공
 * - 챕터 생성 중 thinking text 표시
 */
const ChapterSection = observer(() => {
    const chapterStore = useChapterStore();

    return (
        <SentenceProvider sentence={chapterStore.currentSentence}>
            <div className="mx-auto w-full max-w-[760px]">
                <div className="relative">
                    <ChapterDetailTitle />
                    {/* 챕터 생성 오류 시 재시도 버튼 */}
                    {chapterStore.hasChapterError && (
                        <div className="absolute -bottom-10 left-0">
                            <div className="text-error flex items-center gap-2 text-sm">
                                학습자료 생성 중 오류가 발생했습니다
                                <button
                                    className="btn btn-outline btn-xs"
                                    onClick={() => {
                                        chapterStore.retryCreateChapter();
                                    }}
                                >
                                    다시 만들기
                                </button>
                            </div>
                        </div>
                    )}
                    {/* 챕터 생성 중 (오류 아닌 경우) */}
                    {chapterStore.isCreatingChapter && (
                        <div className="absolute -bottom-12 left-0">
                            {chapterStore.isLastChapterThinkingText ? (
                                <div className="text-error flex items-center gap-2 text-sm">
                                    {chapterStore.chapterThinkingText}
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => {
                                            location.reload();
                                        }}
                                    >
                                        새로고침
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5">
                                    <ThinkingText>{chapterStore.chapterThinkingText}</ThinkingText>
                                    {chapterStore.chapterStatusUpdatedAt && (
                                        <>
                                            <span className="text-primary/70">·</span>
                                            <CountdownText
                                                className="text-primary/70 text-xs"
                                                startTime={chapterStore.chapterStatusUpdatedAt}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div
                    className={twMerge(
                        'mt-10 mb-10 flex min-h-[50dvh] items-center justify-center',
                        // 문장이 짧아도 적당한 높이를 확보하고 있어야 컨트롤 패널이 덜컥거리지 않으면서 학습할 수 있음
                        'min-h-[calc((100dvh-var(--header-height)) / 2)] w-full',
                    )}
                >
                    <SentenceLearning />
                </div>
                <div
                    // 컨트롤 패널은 stick 로 화면 하단에 고정됨
                    // 패널을 블러 처리하기 때문에 하단 여백은 없어야 함
                    className={twMerge('sticky bottom-0 z-20 w-full backdrop-blur')}
                >
                    <ControlPanel />
                </div>
            </div>
        </SentenceProvider>
    );
});

/**
 * LessonPageContent: URL 쿼리 파라미터와 LessonStore 양방향 동기화
 */
const LessonPageContent = observer(() => {
    const lessonStore = useLessonStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const lessonPlayerRef = useRef<LessonPlayerRef>(null);

    // 레슨 플레이어 참조를 store에 설정
    useEffect(() => {
        lessonStore.setLessonPlayerRef(lessonPlayerRef);

        return () => {
            lessonStore.setLessonPlayerRef(null);
        };
    }, [lessonStore, lessonPlayerRef]);

    // 초기화 플래그
    const initializedRef = useRef(false);

    // 초기화: URL → Store (챕터 로드 완료 후 1회만)
    useEffect(() => {
        // 이미 초기화했거나 챕터 목록이 아직 로드되지 않았으면 skip
        if (initializedRef.current || lessonStore.chapterList.length === 0) {
            return;
        }

        const chapterParam = searchParams.get('chapter');
        const sentenceParam = searchParams.get('sentence');

        const chapterNum = chapterParam ? parseInt(chapterParam, 10) : 1;
        const sentenceNum = sentenceParam ? parseInt(sentenceParam, 10) : 1;

        if (!isNaN(chapterNum) && !isNaN(sentenceNum)) {
            lessonStore.setChapter(chapterNum, sentenceNum);
        }

        // 초기화 완료 표시
        initializedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonStore.chapterList.length]); // 챕터 로드 완료 시 1회만

    // 동기화: Store → URL (초기화 완료 후에만)
    useEffect(() => {
        // 초기화 전에는 Store → URL 동기화하지 않음
        if (!initializedRef.current) {
            return;
        }

        const chapter = lessonStore.currentChapterNum;
        const sentence = lessonStore.currentSentenceNum;

        setSearchParams(
            (prev) => {
                prev.set('chapter', String(chapter));
                prev.set('sentence', String(sentence));
                return prev;
            },
            { preventScrollReset: true, replace: true },
        );
    }, [lessonStore.currentChapterNum, lessonStore.currentSentenceNum, setSearchParams]);

    const onSentenceChange = useCallback(
        (sentenceNum: number) => {
            lessonStore?.setSentence(sentenceNum);
        },
        [lessonStore],
    );

    return (
        <>
            <ListeningHeader
                className={twMerge(
                    // 레슨 페이지에서는 헤더 높이를 고정하고 제목 중앙 정렬
                    'h-12 items-center! justify-center!',
                )}
            >
                <h1
                    className={twMerge(
                        'text-base-content/90 bg-base-100 truncate text-base font-normal',
                        lessonStore.isLoading && 'skeleton w-1/2 rounded-sm',
                    )}
                >
                    {lessonStore.lesson?.title || <>&nbsp;</>}
                </h1>
            </ListeningHeader>
            <div
                className={twMerge(
                    'flex w-full flex-row justify-center',
                    // 헤더 높이(h-12)를 제외하고 나머지 영역을 최대 높이로 설정함
                    'h-[calc(100dvh-48px)] overflow-hidden pt-4 pb-4',
                    // 모바일에서는 좌우 간격 없음, 화면 클 때 여백 추가
                    'gap-0 2xl:gap-20',
                )}
            >
                {/* 모바일에서는 상단(영상→챕터), 태블릿은 (영상 + 챕터) 한 줄, 데스크탑은 좌측 도킹 */}
                <section
                    className={twMerge(
                        'flex-2/5',
                        'h-full max-w-[640px] min-w-[320px] overflow-y-scroll overscroll-y-none px-4',
                    )}
                >
                    {/* 영상 */}
                    <div className={'bg-base-100 sticky top-0 z-10 w-full pb-4'}>
                        <LessonPlayer ref={lessonPlayerRef} />
                    </div>
                    {/* Chapter */}
                    <div>
                        <div className="flex h-10 items-center">
                            {/* 레슨 생성 오류 시 재시도 버튼 */}
                            {lessonStore.hasLessonError && (
                                <div className="text-error flex items-center gap-2 text-sm">
                                    레슨 생성 중 오류가 발생했습니다
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => {
                                            lessonStore.retryCreateLesson();
                                        }}
                                    >
                                        다시 만들기
                                    </button>
                                </div>
                            )}
                            {/* 레슨 생성 중 (오류 아닌 경우) */}
                            {lessonStore.isCreatingLesson &&
                                !lessonStore.isLastLessonThinkingText && (
                                    <div className="flex items-center gap-1.5">
                                        <ThinkingText>
                                            {lessonStore.lessonThinkingText}
                                        </ThinkingText>
                                        {lessonStore.lessonStatusUpdatedAt && (
                                            <>
                                                <span className="text-primary/70">·</span>
                                                <CountdownText
                                                    className="text-primary/70 text-xs"
                                                    startTime={lessonStore.lessonStatusUpdatedAt}
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                            {lessonStore.isCreatingLesson &&
                                lessonStore.isLastLessonThinkingText && (
                                    <div className="text-error flex items-center gap-2 text-sm">
                                        {lessonStore.lessonThinkingText}
                                        <button
                                            className="btn btn-outline btn-xs"
                                            onClick={() => {
                                                location.reload();
                                            }}
                                        >
                                            새로고침
                                        </button>
                                    </div>
                                )}
                            {!lessonStore.isCreatingLesson && !lessonStore.hasLessonError && (
                                <div className="text-base-content/70 mb-2 text-xs">
                                    챕터{' '}
                                    {lessonStore.chapterList?.length > 0 &&
                                        `${lessonStore.chapterList.length}개`}
                                </div>
                            )}
                        </div>
                        <ChapterList />
                    </div>
                </section>
                <section
                    className={twMerge(
                        'flex-3/5',
                        'h-full max-w-[800px] overflow-y-scroll overscroll-y-none px-4', // 학습 메인 영역은 화면의 오른쪽 2/3 영역을 차지함
                    )}
                >
                    <ChapterProvider
                        lessonHash={lessonStore?.lessonHash}
                        chapterHash={lessonStore?.currentChapterHash || ''}
                        sentenceNum={lessonStore?.currentSentenceNum}
                        onSentenceChange={onSentenceChange}
                    >
                        <ChapterSection />
                    </ChapterProvider>
                </section>
            </div>
        </>
    );
});

const LessonPage = observer(() => {
    const { lessonHash = '' } = useParams();

    return (
        <LessonProvider lessonHash={lessonHash}>
            <LessonPageContent />
        </LessonProvider>
    );
});

export default LessonPage;
